// AvatarPicker — modal/panel where the user chooses their mascot.
// Persists to backend via PATCH /api/users/me/avatar; falls back gracefully on any error.
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Check, Loader2, Sparkles, Crown } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { AVATAR_CATALOG, AVATAR_IDS } from "@/lib/avatars";
import { listAvatars, updateMyAvatar } from "@/lib/api";
import { Avatar } from "@/components/Avatar";

export const AvatarPicker = ({ open, onClose }) => {
  const { user, refresh } = useAuth();
  const [saving, setSaving] = useState("");
  const [availableIds, setAvailableIds] = useState(AVATAR_IDS.filter((id) => !AVATAR_CATALOG[id].legendary));
  const current = user?.avatar || "fox";
  // Founder / admin accounts have phoenix PERMANENTLY locked by the server. Show a nicer
  // "already locked" state instead of letting them click around only to see a 400.
  const isCreator = !!user && current === "phoenix" && (user?.email === "manicmooncreations@yahoo.com" || user?.email === "admin@lectura.app");

  useEffect(() => {
    if (!open) return;
    listAvatars()
      .then((d) => {
        // Server tells us which mascots this user is allowed to see (includes legendary if creator).
        const ids = (d.avatars || []).filter((id) => AVATAR_CATALOG[id]);
        setAvailableIds(ids.length ? ids : AVATAR_IDS);
      })
      .catch(() => {
        setAvailableIds(AVATAR_IDS.filter((id) => !AVATAR_CATALOG[id].legendary));
      });
  }, [open]);

  const pick = async (id) => {
    if (saving) return;
    setSaving(id);
    try {
      await updateMyAvatar(id);
      await refresh?.();
      toast.success(`Mascot updated → ${AVATAR_CATALOG[id]?.label}`);
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update mascot");
    } finally {
      setSaving("");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-lg p-0 overflow-hidden">
        <DialogTitle className="sr-only">Pick your mascot</DialogTitle>
        <DialogDescription className="sr-only">Choose an animal mascot that reacts live during class games.</DialogDescription>
        <div className="p-6 bg-gradient-to-br from-amber-100/60 to-rose-100/60 dark:from-amber-950/40 dark:to-rose-950/40">
          <div className="inline-flex items-center gap-1.5 rounded-full bg-white/60 dark:bg-black/30 border border-border px-3 py-1 text-[10px] font-bold uppercase tracking-widest">
            <Sparkles className="h-3 w-3 text-accent" /> Pick your mascot
          </div>
          <h2 className="mt-3 font-serif text-3xl font-black">Choose your Lectura Live avatar</h2>
          <p className="mt-1 text-sm text-muted-foreground">Your mascot dances on correct, sulks on wrong, catches fire on a streak, and reacts live on the projector during class games.</p>
          {isCreator && (
            <p data-testid="founder-locked-banner" className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-amber-400/15 border border-amber-400/40 text-amber-800 dark:text-amber-200 px-3 py-1 text-xs font-bold">
              <Crown className="h-3.5 w-3.5" /> Founder mascot — permanently 🐦‍🔥 phoenix
            </p>
          )}
        </div>
        <div className="p-6">
          <ul className="grid grid-cols-4 sm:grid-cols-5 gap-3">
            {availableIds.map((id) => {
              const active = id === current;
              const meta = AVATAR_CATALOG[id] || {};
              const legendary = !!meta.legendary;
              return (
                <li key={id}>
                  <button
                    data-testid={`avatar-pick-${id}`}
                    onClick={() => !isCreator && pick(id)}
                    disabled={!!saving || isCreator}
                    title={isCreator ? "Your founder mascot is locked to phoenix" : meta.label}
                    className={`relative w-full aspect-square rounded-2xl border-2 flex flex-col items-center justify-center gap-1 transition-all ${
                      active
                        ? "border-accent bg-accent/10 shadow-lg scale-[1.02]"
                        : legendary
                        ? "border-amber-400 bg-gradient-to-br from-amber-400/15 via-rose-400/15 to-fuchsia-500/15 shadow-[0_0_16px_rgba(251,191,36,0.35)] hover:from-amber-400/25 hover:to-fuchsia-500/25"
                        : "border-border hover:border-accent/50 hover:bg-accent/5"
                    } ${saving === id ? "opacity-60" : ""}`}
                  >
                    <span className="text-4xl leading-none">{meta.glyph}</span>
                    <span className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">{meta.label}</span>
                    {legendary && (
                      <span className="absolute -top-1.5 -left-1.5 inline-flex items-center gap-0.5 rounded-full bg-gradient-to-r from-amber-400 to-rose-500 text-white px-1.5 py-0.5 text-[8px] font-black uppercase tracking-widest shadow">
                        <Crown className="h-2.5 w-2.5" /> Legend
                      </span>
                    )}
                    {active && (
                      <span className="absolute -top-1.5 -right-1.5 grid place-items-center h-6 w-6 rounded-full bg-accent text-accent-foreground shadow">
                        <Check className="h-3.5 w-3.5" />
                      </span>
                    )}
                    {saving === id && (
                      <span className="absolute inset-0 grid place-items-center bg-background/60 rounded-2xl">
                        <Loader2 className="h-5 w-5 animate-spin text-accent" />
                      </span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Small standalone pill/button that opens the picker
export const AvatarPill = ({ onClick, size = 28 }) => {
  const { user } = useAuth();
  return (
    <button
      data-testid="avatar-pill-btn"
      onClick={onClick}
      className="inline-flex items-center gap-2 rounded-full border border-border bg-secondary/40 px-2 py-1 text-xs font-semibold hover:border-accent/50 transition"
      title="Change your mascot"
    >
      <Avatar id={user?.avatar || "fox"} size={size} />
      <span className="hidden sm:inline text-muted-foreground">{AVATAR_CATALOG[user?.avatar || "fox"]?.label}</span>
    </button>
  );
};

export default AvatarPicker;
